import { useState } from 'react';
import {
    Badge,
    Box,
    Card,
    CardContent,
    Drawer,
    Fab,
    IconButton,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Close as CloseIcon,
    VisibilityOutlined as PreviewIcon,
} from '@mui/icons-material';
import { useAutomations } from '@/hooks/queries/dm-automation';
import { useInstagramStatus } from '@/hooks/queries/instagram';
import { ACCENT, ACCENT_TINT, TRIGGER_ICON, TRIGGER_LABEL, parseTargetPostIds } from './_shared';

interface Props {
    selectedId?: string | null;
}

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography
        variant="subtitle2"
        sx={{
            fontWeight: 700,
            color: 'text.secondary',
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&::before': {
                content: '""',
                display: 'inline-block',
                width: 4,
                height: 14,
                bgcolor: ACCENT,
                borderRadius: 1,
            },
        }}
    >
        {children}
    </Typography>
);

const PreviewPanel = ({ selectedId }: Props) => {
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const [drawerOpen, setDrawerOpen] = useState(false);

    const statusQuery = useInstagramStatus({ withProfile: false });
    const isConnected = statusQuery.data?.isConnected === true;
    const automationsQuery = useAutomations({ enabled: isConnected });

    const automations = automationsQuery.data?.itemList ?? [];
    const selected = selectedId ? automations.find((a) => a.id === selectedId) : undefined;
    const featured = selected ?? automations.find((a) => a.isActive) ?? automations[0];
    const isExplicitSelection = Boolean(selected);

    const renderFeatured = () => {
        if (!featured) return null;
        const TriggerIcon = TRIGGER_ICON[featured.triggerType];
        const targetIds = parseTargetPostIds(featured.targetPost);
        const targetLabel =
            featured.targetPost === 'ALL' || targetIds.length === 0
                ? '모든 게시물'
                : `게시물 ${targetIds.length}개`;

        return (
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                            sx={{
                                width: 28,
                                height: 28,
                                borderRadius: 1.5,
                                bgcolor: ACCENT_TINT,
                                color: ACCENT,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <TriggerIcon sx={{ fontSize: 16 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                {featured.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {TRIGGER_LABEL[featured.triggerType]}
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            이렇게 DM이 발송됩니다
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: 'background.paper',
                                borderRadius: '12px 12px 12px 4px',
                                px: 1.5,
                                py: 1.25,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                            }}
                        >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                {featured.responseMessage}
                            </Typography>
                        </Box>
                        {featured.buttonText && (
                            <Box sx={{ textAlign: 'center', mt: 1 }}>
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        bgcolor: ACCENT,
                                        color: '#fff',
                                        px: 1.75,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        fontSize: 11,
                                        fontWeight: 700,
                                    }}
                                >
                                    {featured.buttonText} →
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Stack spacing={0.5}>
                        {featured.publicReply && (
                            <Typography variant="caption" color="text.secondary">
                                • <b>댓글에 답글</b>: "{featured.publicReply}"
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            • <b>대상 게시물</b>: {targetLabel}
                        </Typography>
                        {featured.buttonLink && (
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                • <b>버튼 링크</b>: {featured.buttonLink}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    const renderBody = () => {
        if (!isConnected) {
            return (
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                            Instagram 계정을 연결하면 발송될 DM 미리보기가 여기에 표시됩니다.
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        if (automationsQuery.isLoading) {
            return <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />;
        }

        if (!featured) {
            return (
                <Card variant="outlined" sx={{ borderRadius: 3, borderStyle: 'dashed' }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            자동화를 만들어보세요
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            여기에 미리보기가 표시돼요
                        </Typography>
                    </CardContent>
                </Card>
            );
        }

        return renderFeatured();
    };

    const titleText = isExplicitSelection
        ? '선택한 자동화 미리보기'
        : featured
          ? '미리보기 (자동 선택)'
          : '미리보기';

    const helperText =
        !isExplicitSelection && featured
            ? '카드를 클릭하면 해당 자동화의 미리보기로 바뀝니다'
            : null;

    const content = (
        <>
            <SectionTitle>{titleText}</SectionTitle>
            {helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    {helperText}
                </Typography>
            )}
            {renderBody()}
        </>
    );

    // 큰 화면: 고정 사이드 패널
    if (isLargeScreen) {
        return (
            <Box
                component="aside"
                sx={{
                    width: 380,
                    flexShrink: 0,
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    overflowY: 'auto',
                    px: 2.5,
                    py: 3,
                }}
            >
                {content}
            </Box>
        );
    }

    // 작은 화면: 우하단 FAB + 우측 Drawer 토글
    return (
        <>
            <Tooltip title="미리보기 열기" placement="left">
                <Fab
                    onClick={() => setDrawerOpen(true)}
                    aria-label="open preview"
                    sx={{
                        position: 'fixed',
                        right: 24,
                        bottom: 24,
                        bgcolor: ACCENT,
                        color: '#fff',
                        cursor: 'pointer',
                        boxShadow: '0 6px 18px rgba(251,109,72,0.35)',
                        zIndex: (theme) => theme.zIndex.drawer - 1,
                        '&:hover': { bgcolor: '#E55A3B' },
                    }}
                >
                    <Badge
                        variant="dot"
                        invisible={!isExplicitSelection}
                        sx={{
                            '& .MuiBadge-badge': {
                                bgcolor: '#fff',
                                outline: `2px solid ${ACCENT}`,
                            },
                        }}
                    >
                        <PreviewIcon />
                    </Badge>
                </Fab>
            </Tooltip>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 380 },
                        bgcolor: 'background.default',
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 2.5,
                        py: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        미리보기
                    </Typography>
                    <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ cursor: 'pointer' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                <Box sx={{ px: 2.5, py: 3, overflowY: 'auto' }}>{content}</Box>
            </Drawer>
        </>
    );
};

export default PreviewPanel;
